import { useEffect, useState } from "react";
import { Input } from "@component/input";
import { Button } from "@component/button";
import { Label } from "@component/label";
import { ArrowRightIcon, EyeIcon, EyeOffIcon } from "lucide-react";
import { authGoogleSSO, authLogin } from "@service/auth";
import { AuthLoginInput } from "@type/auth";
import { Link, useNavigate } from "react-router-dom";
import { FormEvent, FormSubmitEvent } from "@type/events";
import { ButtonSignupGoogle } from "@component/button-signup-google";
import { TokenResponse } from "@react-oauth/google";
import { useQuery } from "@util/useQuery";
import { getCookie, setCookie } from "@util/cookie";
import { getCurrentSubdomain, isSpaSubdomain } from "@util/subdomain";
import { useCompanySubdomain } from "@context/subdomain_context";
import { OURA_COMPANY_TYPE } from "@oura/role";
import { setUserRegister } from "@util/mixpanel";
import { getPostAuthLandingPath, sanitizeRedirectPath } from "@util/auth-redirect";
import BYOCDomainPopup from "./dialog/dialog-wrong-login";
import { SESSION_EXPIRED_FLAG } from "@util/session";
import { VIEW_AS_ENDED_FLAG } from "@util/viewAs";

export function LoginForm() {
  const navigate = useNavigate();
  const [companyLoading, companyInfo] = useCompanySubdomain();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isOpenDialogBYOC, setIsOpenDialogBYOC] = useState(false);
  const [companyBYOCName, setCompanyBYOCName] = useState<string>("");
  const [companyBYOCDomain, setCompanyBYOCDomain] = useState<string>("");
  const [isLoadingGoogle, setIsLoadingGoogle] = useState<boolean>(false);
  const [error, setError] = useState<string | React.ReactNode | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  /**
   * Say why the user is looking at a login form they did not ask for.
   *
   * `endExpiredSession()` sets this flag on the way out when a 401 ended the
   * session (see src/utils/authInterceptor.ts). Landing on a bare login screen
   * mid-task, with no explanation, reads as the app having lost your work —
   * one sentence is the difference between that and an ordinary timeout.
   *
   * Read once and cleared immediately, so it explains this arrival and not the
   * next one. It reuses the form's existing error slot rather than adding a
   * second banner.
   */
  useEffect(() => {
    if (sessionStorage.getItem(SESSION_EXPIRED_FLAG)) {
      sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
      setError("Your session expired. Please sign in again to continue.");
    }
    // A view-as session lasts about two hours. Staff hold no login for the
    // merchant, so "sign in again" would send them hunting for one (#4112).
    if (sessionStorage.getItem(VIEW_AS_ENDED_FLAG)) {
      sessionStorage.removeItem(VIEW_AS_ENDED_FLAG);
      setError("This view of the merchant has ended (it lasts about 2 hours). Close this tab and open View as the merchant again from the staff tab.");
    }
  }, []);
  const queryParams = useQuery()

  /**
   * Whether to offer "Register as ...". An Oura workspace (company type
   * "oura", from the public company lookup for the host's subdomain) is set
   * up by staff and the merchant is given a login: nobody registers there, as
   * an employer or otherwise, so the whole line goes.
   *
   * While that lookup is still on its way on a workspace host the type is not
   * known, and the line is held back rather than offered and then taken away:
   * on a cold load the employer link used to flash on a host that has no
   * employers. A host with no workspace subdomain has no lookup and keeps the
   * line; so does the SPA subdomain, which says so first below.
   */
  const onWorkspaceHost = getCurrentSubdomain() !== null;
  const companyUnknown = onWorkspaceHost && companyLoading && !isSpaSubdomain();
  const showRegister = !companyUnknown && companyInfo?.type !== OURA_COMPANY_TYPE;
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleChange = (e: FormEvent): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormSubmitEvent): Promise<boolean | void> => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    setIsLoadingGoogle(true);
    try {
      const loginPayload: AuthLoginInput = {
        email: formData.email,
        password: formData.password,
      };

      const loginResponse = await authLogin(loginPayload);
      if (loginResponse.status === 200) {
        if (loginResponse?.data?.user?.verification_status === "Unverified") {
          setError(
            <p>Your account is pending approval.<br />You’ll be notified by email once it’s ready to use.</p>
          );
          return false;
        }

        setCookie("authToken", loginResponse.data?.token?.access_token ?? "", import.meta.env.VITE_AUTH_TOKEN_EXPIRED_DAY);

        setUserRegister({
          user_id: loginResponse?.data?.user?.id || "",
          email: loginResponse?.data?.user?.email || "",
          name: loginResponse?.data?.user?.name || "",
          role: loginResponse?.data?.user?.role || "",
        })

        if (loginResponse.data?.cv_missing) {
          setIsLoadingGoogle(false);
          setLoading(false);

          setTimeout(() => {
            setIsLoadingGoogle(false);
            setLoading(false);
            window.location.href = "/resume";
          }, 800)
        } else {
          // The same `?_redirect` hand-off the Google sign-in honours, same-origin paths
          // only: the Setup email's set-password link ends on the Setup tab (#4414).
          const destination = sanitizeRedirectPath(queryParams.get("_redirect"),
            getPostAuthLandingPath(loginResponse?.data?.user?.role));
          setTimeout(() => {
            setIsLoadingGoogle(false);
            setLoading(false);
            window.location.href = destination;
          }, 800)
        }

      } else {
        console.log("Login failed:", loginResponse);
        if (loginResponse.status === 403) {
          if ((loginResponse?.data as any).data?.is_access_expired) {
            setError("Your access to this workspace has expired. Please contact your administrator for assistance.");
            setIsLoadingGoogle(false);
            setLoading(false);
            return false;
          }
          setCompanyBYOCName((loginResponse?.data as any)?.data?.company_name || "");
          setCompanyBYOCDomain((loginResponse?.data as any)?.data?.expected_subdomain || "");
          setIsOpenDialogBYOC(true);
          setIsLoadingGoogle(false);
          setLoading(false);
          setIsOpenDialogBYOC(true);
          return false;
        }
        setError(loginResponse?.message || "");
        console.error(
          "Auto-login failed after registration:",
          loginResponse.message
        );
        setIsLoadingGoogle(false);
        setLoading(false);
        return false;
      }
    } catch (error) {
      console.error("Error during auto-login:", error);
      setError("An error occurred during auto-login.");
      setFormData({
        email: "",
        password: "",
      })
      setIsLoadingGoogle(false);
      setLoading(false);
      return false;
    }
  };

  const handleGoogleLogin = async (tokenResponse: TokenResponse) => {
    setError(null);
    try {
      setIsLoadingGoogle(true);
      // First, fetch user profile information from Google
      const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
        headers: {
          Authorization: `Bearer ${tokenResponse.access_token}`,
        },
      });
      if (!userInfoResponse) {
        setError("Google sign-in failed. Please try again or use another method.");
        setIsLoadingGoogle(false);
        return
      }

      const userInfo = await userInfoResponse.json();
      if (userInfo && userInfo.email === "") {
        setError("We couldn’t access your email from Google. Please allow permission to continue.");
        setIsLoadingGoogle(false);
        return
      }

      const authGoogleSSOResp = await authGoogleSSO({
        email: userInfo.email,
        sub: userInfo.sub,
      })

      if (authGoogleSSOResp.status === 200) {
        setCookie("authToken", authGoogleSSOResp.data?.token?.access_token ?? "", import.meta.env.VITE_AUTH_TOKEN_EXPIRED_DAY);

        setUserRegister({
          user_id: authGoogleSSOResp.data?.user?.id || "",
          email: authGoogleSSOResp.data?.user?.email || "",
          name: authGoogleSSOResp.data?.user?.name || "",
          role: authGoogleSSOResp.data?.user?.role || "",
        })

        const defaultDestination = getPostAuthLandingPath(authGoogleSSOResp.data?.user?.role);
        // Honour the optional `?_redirect` hand-off, but only when it
        // points to a same-origin relative path. Anything else is
        // treated as an open-redirect attempt and we fall back to the
        // role's default landing path.
        const requestedRedirect = queryParams.get("_redirect");
        const safeDestination = sanitizeRedirectPath(requestedRedirect, defaultDestination);
        // cv_missing users must still be forced into the resume upload gate —
        // the resume flow wins over the normal/handed-off destination so an
        // incomplete profile can't bypass onboarding.
        const destination = authGoogleSSOResp.data?.cv_missing ? "/resume" : safeDestination;

        setTimeout(() => {
          window.location.href = destination;
        }, 800)
      } else if (authGoogleSSOResp.status === 403) {
        setFormData((prev) => ({ ...prev, email: userInfo.email || "" }));
        setCompanyBYOCName((authGoogleSSOResp?.data as any)?.data?.company_name || "");
        setCompanyBYOCDomain((authGoogleSSOResp?.data as any)?.data?.expected_subdomain || "");
        setIsOpenDialogBYOC(true);
      } else {
        setError(authGoogleSSOResp.message || "Google sign-in failed. Please try again or use another method.");
      }
      setIsLoadingGoogle(false);
    } catch (error) {
      setError("We couldn’t verify your Google login. Please try again.");
      setIsLoadingGoogle(false);
    }
  }

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      <div className="w-full p-6 rounded-lg border border-gray-200 bg-white text-gray-900 shadow-sm sm:mt-0 mt-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <h2 className="text-lg md:text-2xl font-bold tracking-tight text-center">
              Login to Your Account
            </h2>
            <p className="text-sm text-muted-foreground text-center">
              Enter your credentials to access your <span className="text-blue-600">ourteam</span> account.
            </p>
          </div>
          {error && (
            <div className="text-red-500 text-sm">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="login-email" className="text-gray-900">
                Email
              </Label>
              <Input
                name="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                className="border-gray-300 focus:border-gray-400 focus:ring-gray-400"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="login-password" className="text-gray-900">
                  Password
                </Label>
                <Button
                  type="button"
                  variant="link"
                  onClick={() => navigate("/forgot-password")}
                  className="p-0 h-auto text-xs text-gray-600"
                >
                  Forgot password?
                </Button>
              </div>
              <div className="relative">
                <Input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="border-gray-300 focus:border-gray-400 focus:ring-gray-400"
                />
                <button
                  type="button"
                  onClick={toggleShowPassword}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? (
                    <EyeOffIcon className="h-4 w-4" />
                  ) : (
                    <EyeIcon className="h-4 w-4" />
                  )}
                </button>
              </div>

            </div>

            <div className="flex flex-col gap-2">
              <Button
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? "Please wait" : (
                  <>
                    Login <ArrowRightIcon size={16} />
                  </>
                )}
              </Button>

              <div className="relative mt-3 mb-3">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-300 dark:border-gray-700" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white dark:bg-gray-900 px-2 text-gray-500 dark:text-gray-400">
                    Or continue with
                  </span>
                </div>
              </div>

              <ButtonSignupGoogle
                label="Sign in with Google"
                isLoading={isLoadingGoogle}
                onClick={(tokenResponse) => handleGoogleLogin(tokenResponse)}
              />

              {showRegister && (
              <div className="text-center text-sm mt-2">
                Don't have an account?<br /> Register as{" "}
                {/* SPA is an assessment partner: its subdomain is where their
                    candidates sign up, and nobody registers as an employer
                    there. Checked FIRST, and off the hostname rather than the
                    CURRENT_COMPANY cookie — the cookie is only written after
                    SubdomainProvider's company lookup resolves, so on a cold
                    load it is briefly empty and this block would offer the
                    employer path on a domain that has no employers. */}
                {isSpaSubdomain() ? (
                  <Link
                    to="/candidate-signup"
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                  >
                    Candidate
                  </Link>
                ) : (getCookie("CURRENT_COMPANY") || "") !== "" ? (
                  <Link
                    to="/employer-signup"
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                  >
                    Employer
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/candidate-signup"
                      className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                    >
                      Candidate
                    </Link>{" "}
                    or{" "}
                    <Link
                      to="/employer-signup"
                      className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                    >
                      Employer
                    </Link>
                  </>
                )}

              </div>
              )}
            </div>
          </form>
        </div>
      </div>

      <BYOCDomainPopup
        isOpen={isOpenDialogBYOC}
        onClose={() => setIsOpenDialogBYOC(false)}
        userEmail={formData.email}
        companyName={companyBYOCName}
        companyDomain={companyBYOCDomain}
        autoRedirect
      />
    </>
  );
}
